<script>

import bem from '@/utils/bem';
import HelpCenterCategory from '@/components/helpCenters/helpCenterCategory/Help-center-category.vue';
import HelpCenterHint from '@/components/helpCenters/helpCenterHint/Help-center-hint.vue';
import CategoryHelpCenterModal from '@/components/helpCenters/CategoryHelpCenterModal/Category-help-center-modal.vue';
import HintHelpCenterModal from '@/components/helpCenters/hintHelpCenterModal/Hint-help-center-modal.vue';
import notificationWithIcon from '@/utils/notification';

const b = bem('help-centers');

export default {
  components: {
    HintHelpCenterModal, CategoryHelpCenterModal, HelpCenterHint, HelpCenterCategory,
  },
  props: {
    helpCenters: {
      type: Array,
      required: true,
    },
    categories: {
      type: Array,
    },
    editHint: {
      type: Function,
      required: true,
      default: () => {},
    },
    editCategory: {
      type: Function,
      default: () => {},
    },
    createHint: {
      type: Function,
      required: true,
      default: () => {},
    },
    createCategory: {
      type: Function,
      default: () => {},
    },
    withoutCategory: {
      type: Boolean,
      default: false,
    },
    changePosition: {
      type: Function,
    },
    deleteHint: {
      type: Function,
      required: true,
    },
    deleteCategory: {
      type: Function,
    },
    withCreatedLink: {
      type: Boolean,
      default: false,
    },
    createdLinkPage: {
      type: String,
    },
  },
  data() {
    return {
      isShowHintModal: false,
      isShowCategoryModal: false,
      changeableItem: null,
    };
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    changeOrder({
      list,
      changeableItem,
      changeType,
    }) {
      const foundIndex = list.findIndex((item) => item.order === changeableItem.order);
      if (typeof foundIndex === 'number') {
        const secondChangeableItem = list[foundIndex + (changeType === 'up' ? -1 : 1)];
        this.changePosition([{
          [changeableItem.isCategory
            ? 'categoryId'
            : 'id'
          ]: changeableItem.id || changeableItem.categoryId,
          order: secondChangeableItem.order,
        }, {
          [secondChangeableItem.isCategory
            ? 'categoryId'
            : 'id'
          ]: secondChangeableItem.id || secondChangeableItem.categoryId,
          order: changeableItem.order,
        }]);
      }
    },
    onDelete(item) {
      if (item.isCategory) {
        this.deleteCategory(item.categoryId);
      } else {
        this.deleteHint(item.id);
      }
    },
    onEditHint(item) {
      this.changeableItem = item;
      if (item.isCategory) {
        this.isShowCategoryModal = true;
      } else {
        this.isShowHintModal = true;
      }
    },
    onSubmitCategory(value) {
      if (value.categoryId) {
        this.editCategory(value);
      } else {
        this.createCategory(value);
      }
    },
    onSubmitHint(value) {
      if (value.id) {
        this.editHint(value);
      } else {
        this.createHint(value);
      }
    },
    copyLinkToClipboard(value) {
      const {
        categoryId,
        id,
        title,
      } = value;
      navigator.clipboard.writeText(
        `https://foreverstudios.com/forever-care/?${categoryId ? `categoryId=${categoryId}` : ''}${categoryId && id ? '&' : ''}${id ? `hintId=${id}` : ''}`,
      )
        .then(() => {
          notificationWithIcon('success', `${title} link copied!`);
        })
        .catch(() => {
          notificationWithIcon('error', 'Error copying link');
        });
    },
  },
};
</script>

<template>
  <div :class="b('')">
    <div :class="b('list')">
      <template
        v-for="(item, index) in helpCenters"
      >
        <help-center-category
          v-if="item.isCategory"
          :category="item"
          :isFirst="index === 0"
          v-model:changeableItem="changeableItem"
          :isLast="index === helpCenters.length - 1"
          :key="'category-' + item.categoryId"
          :onEdit="onEditHint"
          :onDelete="onDelete"
          :changeOrder="(changeType) => changeOrder({
            list: helpCenters,
            changeableItem: item,
            changeType
          })"
          :withCreatedLink="withCreatedLink"
          :copyLinkToClipboard="copyLinkToClipboard"
          :changeOrderHint="changeOrder"
        />
        <help-center-hint
          v-else
          :hint="item"
          :isFirst="index === 0"
          :onEdit="() => onEditHint(item)"
          :isLast="index === helpCenters.length - 1"
          :key="'hint-' + item.id"
          :onDelete="() => onDelete(item)"
          :changeOrder="(changeType) => changeOrder({
            list: helpCenters,
            changeableItem: item,
            changeType
          })"
          :withCreatedLink="withCreatedLink"
          :copyLinkToClipboard="copyLinkToClipboard"
        />
      </template>
    </div>
    <div :class="b('buttons')">
      <a-button
        v-if="!withoutCategory"
        type="primary"
        html-type="button"
        @click="() => isShowCategoryModal = true"
      >
        Create new category
      </a-button>
      <a-button
        type="primary"
        html-type="button"
        @click="() => isShowHintModal = true"
      >
        Create new hint
      </a-button>
    </div>
    <category-help-center-modal
      v-model:show="isShowCategoryModal"
      v-model:category="changeableItem"
      :onSubmit="onSubmitCategory"
    />
    <hint-help-center-modal
      v-model:show="isShowHintModal"
      v-model:hint="changeableItem"
      :onSubmit="onSubmitHint"
      :categories="categories || []"
      :withoutCategory="withoutCategory"
    />
  </div>
</template>

<style scoped lang="scss">
  @import '@/styles/variables';
  @import "@/styles/media";
  @import "@/styles/fonts";

  .help-centers {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 1544px;
    margin: 0 auto;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1544);
    }

    &__list {
      padding: 10px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      max-height: 750px;
      overflow-y: scroll;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(750);
      }
    }

    &__buttons {
      display: flex;
      margin: 0 auto;
      gap: 30px;

      @include mediaMinMax(1025, $normal) {
        gap: vw-full(30);
      }
    }
  }
</style>
